// frontend/src/components/Layout/FooterNew.tsx
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../hooks/useLanguage';
import { useClinicInfo, useClinicDefaultSchedule, ClinicDefaultWeekDay } from '../../services/api';

const DAYS_LABELS: Record<string, { weekdays: string; saturday: string; sunday: string }> = {
  ru: { weekdays: 'Пн-Пт:', saturday: 'Суббота:', sunday: 'Воскресенье:' },
  en: { weekdays: 'Mon-Fri:', saturday: 'Saturday:', sunday: 'Sunday:' },
  hy: { weekdays: 'Երկ-Ուրբ:', saturday: 'Շաբաթ:', sunday: 'Կիրակի:' },
};
const CLOSED_LABEL: Record<string, string> = {
  ru: 'Выходной', en: 'Closed', hy: 'Փակ',
};

function formatTime(t: string | null): string {
  if (!t) return '';
  return t.substring(0, 5);
}

function buildWorkingHoursFromSchedule(
  schedule: ClinicDefaultWeekDay[],
  lang: string
): { weekdaysTime: string; saturdayTime: string; sundayTime: string } {
  const byDay: Record<number, ClinicDefaultWeekDay> = {};
  schedule.forEach(d => { byDay[d.day_of_week] = d; });

  const closed = CLOSED_LABEL[lang] || CLOSED_LABEL.ru;

  const weekday = byDay[0];
  const saturday = byDay[5];
  const sunday = byDay[6];

  const weekdaysTime = weekday?.is_working
    ? `${formatTime(weekday.start_time)} - ${formatTime(weekday.end_time)}`
    : closed;
  const saturdayTime = saturday?.is_working
    ? `${formatTime(saturday.start_time)} - ${formatTime(saturday.end_time)}`
    : closed;
  const sundayTime = sunday?.is_working
    ? `${formatTime(sunday.start_time)} - ${formatTime(sunday.end_time)}`
    : closed;

  return { weekdaysTime, saturdayTime, sundayTime };
}

const FooterNew: React.FC = () => {
  const { t, language } = useLanguage();
  const currentYear = new Date().getFullYear();
  const { data: clinicInfo } = useClinicInfo();
  const { data: defaultSchedule } = useClinicDefaultSchedule();
  const clinic = clinicInfo?.[0];

  const lang = (language as string) || 'ru';
  const labels = DAYS_LABELS[lang] || DAYS_LABELS.ru;
  const hours = defaultSchedule
    ? buildWorkingHoursFromSchedule(defaultSchedule, lang)
    : { weekdaysTime: '…', saturdayTime: '…', sundayTime: '…' };
  const location = useLocation();

  const navLinks = [
    { path: '/', label: t('navigation.home') },
    { path: '/about', label: t('navigation.about') },
    { path: '/departments', label: t('navigation.departments') },
    { path: '/doctors', label: t('navigation.doctors') },
    { path: '/services', label: t('navigation.servicesAndPrice') },
    { path: '/contact', label: t('navigation.contact') },
  ];

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <footer className="px-4 sm:px-8 py-6 bg-[#fdfdfd] dark:bg-[#121212]">
      <div
        className="max-w-[1312px] mx-auto rounded-3xl overflow-hidden relative p-6 sm:p-10 lg:p-16 flex flex-col gap-8"
        style={{
          background: 'radial-gradient(ellipse at 50% 110%, #5b9bff 0%, #448af3 25%, #2d6be7 50%, #1753db 75%, #0b47d5 87.5%, #003bcf 100%)',
        }}
      >
        {/* Декоративные эллипсы */}
        <div className="absolute bottom-0 right-[-135px] size-[656px] mix-blend-soft-light pointer-events-none">
          <div className="absolute inset-[-24.62%]">
            <img alt="" className="block max-w-none size-full" src="/images/decorative/hero-ellipse.svg" />
          </div>
        </div>
        <div className="absolute bottom-[-264px] right-[685px] size-[656px] mix-blend-soft-light pointer-events-none">
          <div className="absolute inset-[-24.62%]">
            <img alt="" className="block max-w-none size-full" src="/images/decorative/hero-ellipse.svg" />
          </div>
        </div>

        {/* Main row */}
        <div className="relative flex flex-col lg:flex-row gap-10 lg:gap-14 items-start">

          {/* Col 1 — Logo + legal */}
          <div className="flex flex-col gap-2.5 flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <img src="/logo.svg" alt={clinic?.name || 'Elit-Med'} className="w-12 h-12" />
              <span className="text-[#eeeef0] text-xl font-bold">{clinic?.name || 'Elit-Med'}</span>
            </div>
            <p className="text-[#eeeef0] text-sm leading-6 mt-2">
              © {currentYear} Elit-Med Clinic. {t('footer.allRightsReserved')}
            </p>
            <div className="flex flex-col gap-1">
              <Link to="/privacy" className="text-[#c2dbff] text-sm font-medium underline-offset-2 hover:underline hover:text-white transition-colors self-start">
                {t('footer.legal.privacy')}
              </Link>
              <Link to="/terms" className="text-[#c2dbff] text-sm font-medium underline-offset-2 hover:underline hover:text-white transition-colors self-start">
                {t('footer.legal.terms')}
              </Link>
            </div>
          </div>

          {/* Col 2 — Navigation */}
          <div className="flex flex-col gap-1">
            <p className="text-[#eeeef0] text-base font-bold leading-9">{t('footer.quickLinks')}</p>
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`py-2 rounded-xl text-base font-medium transition-colors ${
                  isActive(link.path)
                    ? 'bg-[#fdfdfd] dark:bg-[#003bcf] text-[#0c63e9] dark:text-[#eeeef0] px-2'
                    : 'text-[#eeeef0] hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Col 3 — Working hours */}
          <div className="flex flex-col gap-1 text-[#eeeef0] text-base">
            <p className="font-bold leading-9">{t('footer.workingHours.title')}</p>
            <div className="flex flex-col">
              <div className="flex items-center justify-between gap-4 py-2">
                <span className="font-bold shrink-0">{labels.weekdays}</span>
                <span className="font-medium text-right">{hours.weekdaysTime}</span>
              </div>
              <div className="flex items-center justify-between gap-4 py-2">
                <span className="font-bold shrink-0">{labels.saturday}</span>
                <span className="font-medium text-right">{hours.saturdayTime}</span>
              </div>
              <div className="flex items-center justify-between gap-4 py-2">
                <span className="font-bold shrink-0">{labels.sunday}</span>
                <span className="font-medium text-right">{hours.sundayTime}</span>
              </div>
            </div>
            <Link
              to="/clinic-schedule"
              className="text-[#c2dbff] hover:text-white text-sm underline-offset-2 hover:underline mt-1 self-start"
            >
              {t('clinicSchedule.viewFull')} →
            </Link>
          </div>

          {/* Col 4 — Contacts */}
          <div className="flex flex-col gap-1">
            <p className="text-[#eeeef0] text-base font-bold leading-9">{t('footer.contact')}</p>
            <div className="flex flex-col">
              {clinic?.phones?.map((phone) => (
                <a
                  key={phone}
                  href={`tel:${phone}`}
                  className="flex items-center gap-2.5 py-2 text-[#eeeef0] text-base font-medium hover:text-white transition-colors"
                >
                  {/* Phone icon */}
                  <svg className="w-6 h-6 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24 11.47 11.47 0 003.58.57 1 1 0 011 1V21a1 1 0 01-1 1A17 17 0 013 5a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.45.57 3.58a1 1 0 01-.25 1.01l-2.2 2.2z" />
                  </svg>
                  {phone}
                </a>
              ))}
              {clinic?.email && (
                <a
                  href={`mailto:${clinic.email}`}
                  className="flex items-start gap-2.5 py-2 text-[#eeeef0] text-base font-medium hover:text-white transition-colors min-w-0"
                >
                  {/* Email icon */}
                  <svg className="w-6 h-6 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="break-all">{clinic.email}</span>
                </a>
              )}
              {clinic?.address && (
                <div className="flex items-start gap-2.5 py-2 text-[#eeeef0] text-base font-medium min-w-0">
                  {/* Location icon */}
                  <svg className="w-6 h-6 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="break-words">{clinic.address}</span>
                </div>
              )}
            </div>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default FooterNew;
